import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, catchError, of } from 'rxjs';
import { TutorService } from '../../../../services/tutor.service';
import { AGENT_SETUP_TABS, AgentSetupTab, AgentSetupTabKey } from '../../../agent-setup/agent-setup-tabs';
import { AI_SETTINGS_EMBEDDED_SECTION } from '../ai-settings/ai-settings.component';

/**
 * Agent › Verhalten & Fähigkeiten — the same four tabs for one agent
 * (/tutors/:id/setup/<key>). A tab is shown only when the agent's template
 * carries its switch (tutor_basic has no tool loop: no skills, no MCP);
 * Regeln & Grenzen always. Tabs appear once the agent's settings are loaded,
 * so a deep link is not bounced to another tab while they are on their way.
 */
@Component({
  selector: 'app-tutor-setup',
  templateUrl: './tutor-setup.component.html',
  styleUrl: './tutor-setup.component.scss',
  // Reiter „Moodle-Aktionen" = KI-Konfiguration mit festem Abschnitt.
  providers: [{ provide: AI_SETTINGS_EMBEDDED_SECTION, useValue: 'moodle_actions' }],
})
export class TutorSetupComponent implements OnInit, OnDestroy {
  tutorId = '';
  tabs: AgentSetupTab[] = [];
  ready = false;
  active: AgentSetupTabKey = 'learnings';
  private wanted: string | null = null;
  private subs: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private tutorService: TutorService
  ) {}

  ngOnInit(): void {
    this.tutorId = this.route.parent?.snapshot.paramMap.get('tutorId') ?? '';
    this.subs.push(
      this.route.paramMap.subscribe((params) => {
        this.wanted = params.get('tab');
        this.select();
      }),
      // Eigene, frische Abfrage für genau diesen Agenten statt des geteilten
      // `tutorSettings$`: der hält beim Wechsel noch den vorigen Agenten —
      // war das ein Twin ohne Moodle-Einstellungen, sprang ein Direktlink
      // (oder die Weiterleitung /ai-settings/moodle_actions) auf Learnings.
      // Scheitert die Abfrage, bleiben die Reiter ohne Einstellung.
      this.tutorService.fetchTutorSettings(this.tutorId).pipe(
        catchError(() => of([]))
      ).subscribe((settings) => {
        const names = new Set((settings || []).map((s) => s.variable_name));
        const tabs = AGENT_SETUP_TABS.filter((t) => !t.setting || names.has(t.setting));
        // Same instance while the set is unchanged — no tab rebuild.
        if (tabs.map((t) => t.key).join() !== this.tabs.map((t) => t.key).join()) this.tabs = tabs;
        this.ready = true;
        this.select();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  private select(): void {
    if (!this.ready) return;
    const hit = this.tabs.find((t) => t.key === this.wanted);
    if (hit) {
      this.active = hit.key;
    } else {
      this.router.navigate(['/tutors', this.tutorId, 'setup', this.tabs[0].key], { replaceUrl: true });
    }
  }

  onTabChange(tab: { tabId: string }): void {
    if (tab?.tabId && tab.tabId !== this.active) {
      this.router.navigate(['/tutors', this.tutorId, 'setup', tab.tabId], { replaceUrl: true });
    }
  }

  trackTab = (_: number, t: AgentSetupTab) => t.key;
}
